import {
  ForeignTradeBackendFieldError,
  trimBackendWhitespace,
  validateLatinRequisite
} from '@/shared/lib/foreignTradeText';
import {
  OrganizationRegistrationDraft,
  OrganizationRegistrationFormState,
  OrganizationRegistrationModifiedFields,
  OrganizationRegistrationStage,
  OrganizationRegistrationTouched,
  OrganizationRegistrationValidation
} from './types';
import { RegisterOrganizationPayload } from '../../model/types';

interface CreateOrganizationRegistrationDraftPayload {
  form: OrganizationRegistrationFormState;
  innInput: string;
  isInnTouched: boolean;
  isOfferAccepted: boolean;
  modifiedFields?: OrganizationRegistrationModifiedFields;
  stage: OrganizationRegistrationStage;
  submitAttempted: boolean;
  touched: OrganizationRegistrationTouched;
}

export const createOrganizationRegistrationDraft = (
  payload: CreateOrganizationRegistrationDraftPayload
): OrganizationRegistrationDraft => {
  const {
    form,
    innInput,
    isInnTouched,
    isOfferAccepted,
    modifiedFields,
    stage,
    submitAttempted,
    touched
  } = payload;

  return {
    form,
    innInput,
    isInnTouched,
    isOfferAccepted,
    ...(modifiedFields ? { modifiedFields } : {}),
    stage,
    submitAttempted,
    touched
  };
};

export const getInnInputError = (innInput: string): string => {
  const trimmedInn = innInput.trim();

  if (!trimmedInn) {
    return 'Введите ИНН';
  }

  return trimmedInn.length === 10 || trimmedInn.length === 12 ? '' : 'ИНН должен быть 10 или 12 цифр';
};

export const getOrgRegistrationAddressEnError = (value: string): string => {
  const result = validateLatinRequisite(value, { isRequired: false, maxLength: 500 });

  if (result.isValid) return '';

  switch (result.reason) {
    case 'not_latin':
      return 'Введите юридический адрес латиницей';
    case 'no_letters':
      return 'Укажите значение, содержащее буквы';
    case 'too_long':
      return 'Максимум 500 символов';
    default:
      return '';
  }
};

export const getOrgRegistrationFullNameEnError = (value: string): string => {
  const result = validateLatinRequisite(value, { isRequired: true, maxLength: 300 });

  if (result.isValid) return '';

  switch (result.reason) {
    case 'empty':
      return 'Введите полное наименование латиницей';
    case 'not_latin':
      return 'Введите полное наименование латиницей';
    case 'no_letters':
      return 'Укажите значение, содержащее буквы';
    case 'too_long':
      return 'Максимум 300 символов';
    default:
      return '';
  }
};

export const getOrgRegistrationBackendFieldErrorText = (
  field: 'address' | 'addressEn' | 'fullNameEn',
  error: ForeignTradeBackendFieldError
): string => {
  if (field === 'address') {
    if (error.expected) {
      return error.expected;
    }
    if (error.message) {
      return error.message;
    }
    const reason = error.reason?.toLowerCase();
    if (reason === 'required' || reason === 'empty') {
      return 'Укажите юридический адрес';
    }
    return error.reason || 'Неверный адрес';
  }

  if (error.message) return error.message;

  const expected = error.expected?.toLowerCase();
  const reason = error.reason?.toLowerCase();

  if (expected === 'latin' || reason?.includes('latin') || reason === 'required' || reason === 'empty') {
    return field === 'fullNameEn'
      ? 'Введите полное наименование латиницей'
      : 'Введите юридический адрес латиницей';
  }

  if (reason === 'no_letters') {
    return 'Укажите значение, содержащее буквы';
  }

  if (reason === 'too_long') {
    return field === 'fullNameEn'
      ? 'Максимум 300 символов'
      : 'Максимум 500 символов';
  }

  return error.reason || 'Неверное значение';
};

export const getOrganizationRegistrationValidation = (
  form: OrganizationRegistrationFormState
): OrganizationRegistrationValidation => {
  const isIndividualEntrepreneur = form.inn.trim().length === 12;
  const phoneDigits = form.phone.replace(/\D/g, '');
  const minPhoneLength = form.phone.startsWith('+86') ? 13 : 11;

  const trimmedAddress = form.address.trim();
  const trimmedFullName = form.fullName.trim();
  const trimmedKpp = form.kpp.trim();
  const trimmedOgrn = form.ogrn.trim();
  const trimmedShortName = form.shortName.trim();

  const addressEnError = getOrgRegistrationAddressEnError(form.addressEn);

  let addressError = '';
  if (isIndividualEntrepreneur) {
    if (!trimmedAddress) {
      addressError = 'Укажите адрес места жительства';
    } else if (!form.isAddressSelected) {
      addressError = 'Выберите адрес из списка подсказок';
    } else if (form.addressCompleteError) {
      addressError = form.addressCompleteError;
    }
  } else {
    if (!trimmedAddress) {
      addressError = 'Укажите юридический адрес';
    }
  }

  const fullNameEnError = getOrgRegistrationFullNameEnError(form.fullNameEn);
  const fullNameError = !trimmedFullName ? 'Укажите полное наименование' : '';
  const kppError = isIndividualEntrepreneur
    ? ''
    : !trimmedKpp
      ? 'Укажите КПП'
      : trimmedKpp.length !== 9
        ? 'КПП должен быть 9 цифр'
        : '';
  const ogrnError = !trimmedOgrn
    ? isIndividualEntrepreneur
      ? 'Укажите ОГРНИП'
      : 'Укажите ОГРН'
    : isIndividualEntrepreneur
      ? trimmedOgrn.length !== 15
        ? 'ОГРНИП должен быть 15 цифр'
        : ''
      : trimmedOgrn.length !== 13
        ? 'ОГРН должен быть 13 цифр'
        : '';
  const phoneError = phoneDigits.length < minPhoneLength ? 'Введите номер телефона полностью' : '';
  const shortNameError = !trimmedShortName ? 'Укажите сокращенное наименование' : '';

  const areFormFieldsValid = !addressEnError
    && !addressError
    && !fullNameEnError
    && !fullNameError
    && !kppError
    && !ogrnError
    && !phoneError
    && !shortNameError;

  return {
    addressEnError,
    addressError,
    areFormFieldsValid,
    fullNameEnError,
    fullNameError,
    isIndividualEntrepreneur,
    kppError,
    ogrnError,
    phoneError,
    shortNameError
  };
};

export const toRegisterOrganizationPayload = (
  form: OrganizationRegistrationFormState
): RegisterOrganizationPayload => {
  const trimmedAddressFiasId = form.addressFiasId?.trim();
  const trimmedKpp = form.kpp.trim();

  return {
    address: form.address.trim(),
    ...(trimmedAddressFiasId ? { addressFiasId: trimmedAddressFiasId } : {}),
    fullName: form.fullName.trim(),
    fullNameEn: trimBackendWhitespace(form.fullNameEn),
    inn: form.inn.trim(),
    ...(trimmedKpp ? { kpp: trimmedKpp } : {}),
    ogrn: form.ogrn.trim(),
    phone: form.phone,
    shortName: form.shortName.trim()
  };
};